import React, { useEffect, useState, useRef, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Loader2, ArrowLeft, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import Header from "../components/Header";
import Footer from "../components/Footer";
import PropertyConfirm from "../components/valuation/PropertyConfirm";
import ValuationResult from "../components/valuation/ValuationResult";
import CompsGrid from "../components/valuation/CompsGrid";
import NetSheet from "../components/valuation/NetSheet";
import CommissionComparison from "../components/valuation/CommissionComparison";
import NetProceedsSnapshot from "../components/valuation/NetProceedsSnapshot";
import { track } from "../lib/track";
import LeadCaptureModal from "../components/valuation/LeadCaptureModal";
import BlendedEstimate from "../components/valuation/BlendedEstimate";
import MarketNarrative from "../components/valuation/MarketNarrative";
import AIPanelConsensus from "../components/valuation/AIPanelConsensus";
import SpecRefine from "../components/valuation/SpecRefine";
import EstimateChat from "../components/valuation/EstimateChat";
import PropertyMap from "../components/valuation/PropertyMap";
import CMALeadCard from "../components/valuation/CMALeadCard";
import HowItWorksCard from "../components/valuation/HowItWorksCard";
import api, { API } from "../lib/api";
import { apiErr, blendEstimates } from "../lib/format";
import { usePageSeo } from "../lib/seo";
import { gaEvent, trackReportConversion } from "../lib/ga";
import { ENTITY_SITE, brokerageNode, agentNode, breadcrumbNode } from "../lib/entityGraph";

export default function Valuation() {
  const [params] = useSearchParams();
  const address = params.get("address") || "";
  const unit = params.get("unit") || "";

  const valuationCanonical = `${ENTITY_SITE}/valuation`;
  usePageSeo({
    canonical: valuationCanonical,
    jsonLd: {
      "@context": "https://schema.org",
      "@graph": [
        brokerageNode(),
        agentNode(),
        breadcrumbNode(`${valuationCanonical}#breadcrumb`, [
          { name: "Home", item: `${ENTITY_SITE}/` },
          { name: "Valuation Estimate", item: valuationCanonical },
        ]),
      ],
    },
  });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [property, setProperty] = useState(null);
  const [confirmed, setConfirmed] = useState(false);
  const [cities, setCities] = useState([]);
  const [inputs, setInputs] = useState(null);
  const [result, setResult] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [ownerEstimates, setOwnerEstimates] = useState({ zestimate: "", redfin: "", homescom: "", zestimateLive: false });
  const [liveZestimate, setLiveZestimate] = useState(null);
  const [baseProperty, setBaseProperty] = useState(null);
  const [panel, setPanel] = useState(null);
  const [panelLoading, setPanelLoading] = useState(false);
  const debRef = useRef(null);

  const handlePanel = (res) => {
    setPanel(res);
    setPanelLoading(res === null);
    if (res?.ok && res.consensus_value) setInputs((prev) => (prev ? { ...prev, sale_price: res.consensus_value } : prev));
  };

  const applyProperty = (p) => {
    setProperty(p);
    setInputs((prev) => (prev ? { ...prev, sale_price: p.valuation.estimate } : prev));
  };

  useEffect(() => {
    document.title = property?.address?.line1
      ? `${property.address.line1}, ${property.address.city} — Home Value | Equitas`
      : "Your Home Value — Equitas Home Value";
  }, [property]);

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true); setError(null);
      setOwnerEstimates({ zestimate: "", redfin: "", homescom: "", zestimateLive: false });
      setLiveZestimate(null);
      try {
        const cfg = await api.get("/config");
        if (active) setCities(cfg.data.cities);
        const { data } = await api.post("/property/search", { address, unit });
        if (!active) return;
        if (!data.ok) { setError(data.error?.message || "We couldn't find this property."); return; }
        setProperty(data.property);
        setBaseProperty(data.property);
        track("address_search", { address, city: data.property.address?.city, zip: data.property.address?.zip });
        gaEvent("view_valuation_report", {
          city: data.property.address?.city,
          zip: data.property.address?.zip,
          estimate: data.property.valuation?.estimate,
          demo: !!data.property.is_demo,
          has_live_zestimate: !!data.property.zillow_zestimate?.value,
        });
        const lz = data.property.zillow_zestimate;
        if (lz?.value) {
          setLiveZestimate(lz);
          setOwnerEstimates((p) => ({ ...p, zestimate: String(lz.value), zestimateLive: true }));
        }
        const est = data.property.valuation.estimate;
        setInputs({
          sale_price: est, city: data.property.address.city, mortgage_payoff: 0,
          listing_fee_pct: cfg.data.cost_settings.equitas_listing_fee_pct || 1,
          buyer_side_enabled: true, buyer_side_pct: cfg.data.cost_settings.buyer_side_default_pct || 2.5,
          dual_agency: false, escrow_seller_pct: 100,
          title_insurance: cfg.data.cost_settings.title_insurance_default || 0,
          hoa_fees: 0, repairs_credits: 0, staging: 0, other_liens: 0, tax_allocations: {},
        });
      } catch (e) {
        if (active) setError(apiErr(e.response?.data?.detail) || "Something went wrong.");
      } finally { if (active) setLoading(false); }
    })();
    return () => { active = false; };
  }, [address, unit]);

  const compute = useCallback(async (inp) => {
    try {
      const { data } = await api.post("/net-sheet/compute", { inputs: inp });
      setResult(data);
    } catch (e) { /* keep previous result */ }
  }, []);

  useEffect(() => {
    if (!inputs) return;
    clearTimeout(debRef.current);
    debRef.current = setTimeout(() => compute(inputs), 250);
    return () => clearTimeout(debRef.current);
  }, [inputs, compute]);

  const handleFlag = async (note) => {
    await api.post("/property/flag", { address: property.address.normalized, corrections: { note } });
    toast.success("Thank you — our team will review the flagged details.");
  };

  const handleBrokerReview = async () => {
    toast.info("Use the contact form on equitashomes.com or call (408) 561-1321 for a broker-reviewed valuation.");
  };

  const handleReportSubmit = async (contact) => {
    const { data } = await api.post("/reports/request", {
      ...contact,
      property: { ...property, owner_estimates: ownerEstimates, blended: blendEstimates(property.valuation, ownerEstimates, panel) },
      comparables: property.comparables,
      inputs,
    });
    trackReportConversion();
    toast.success("Report ready — your download is starting.");
    window.open(`${API}/reports/download/${data.download_token}`, "_blank");
  };

  if (loading) return <Shell><Center><Loader2 className="h-8 w-8 animate-spin text-brand-emerald" /><p className="mt-4 text-brand-stone">Preparing your valuation…</p></Center></Shell>;

  if (error) return (
    <Shell>
      <Center>
        <AlertCircle className="h-10 w-10 text-brand-gold" />
        <h1 className="mt-4 font-serif text-2xl text-brand-ink">We couldn't complete your valuation</h1>
        <p className="mt-2 max-w-md text-brand-stone">{error}</p>
        <button onClick={handleBrokerReview} className="mt-5 rounded-xl bg-brand-emerald px-6 py-3 text-brand-cream" data-testid="error-broker-review">Request a broker-reviewed valuation</button>
        <Link to="/" className="mt-4 text-sm text-brand-stone hover:text-brand-emerald">← Back home</Link>
      </Center>
    </Shell>
  );

  return (
    <Shell>
      <div className="mx-auto max-w-7xl px-5 sm:px-8 py-10 space-y-12">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-brand-stone hover:text-brand-emerald" data-testid="back-home-link">
          <ArrowLeft className="h-4 w-4" /> New search
        </Link>

        {property.is_demo && (
          <div className="rounded-xl border border-brand-gold/40 bg-brand-goldMuted px-5 py-3 text-sm text-brand-ink" data-testid="demo-mode-banner">
            <strong>Demonstration mode.</strong> Property values and comparable sales shown here are sample data — not live, licensed, or guaranteed figures.
          </div>
        )}

        {!confirmed ? (
          <PropertyConfirm property={property} onConfirm={() => setConfirmed(true)} onFlag={handleFlag} />
        ) : (
          <>
            <div className="grid lg:grid-cols-2 gap-6">
              <ValuationResult property={property} panel={panel} panelLoading={panelLoading} owner={ownerEstimates} liveZestimate={liveZestimate} onBrokerReview={handleBrokerReview} />
              <CommissionComparison result={result} inputs={inputs} setInputs={setInputs} />
            </div>
            <NetProceedsSnapshot result={result} inputs={inputs} />
            <HowItWorksCard />
            {property.valuation?.available && <AIPanelConsensus property={property} onResult={handlePanel} />}
            <BlendedEstimate primary={property.valuation} panel={panel} owner={ownerEstimates} setOwner={setOwnerEstimates} liveZestimate={liveZestimate} address={property.address}
              onUseValue={(v, label) => { setInputs((p) => ({ ...p, sale_price: v })); toast.success(`${label} applied to your net sheet.`); }} />
            {!property.is_demo && (
              <SpecRefine property={property} address={address} unit={unit} isAdjusted={!!property.valuation?.owner_adjusted}
                onRefined={applyProperty} onReset={() => applyProperty(baseProperty)} />
            )}
            {property.valuation?.available && <MarketNarrative property={property} />}
            <PropertyMap subject={property.address} comps={property.comparables} />
            <CompsGrid comparables={property.comparables} />
            <NetSheet property={property} inputs={inputs} setInputs={setInputs} result={result}
              cities={cities} onRequestReport={() => setModalOpen(true)} />
            <CMALeadCard address={property.address?.normalized} estimate={property.valuation?.estimate} />
            {property.valuation?.available && (
              <EstimateChat property={{ ...property, owner_estimates: ownerEstimates }} comparables={property.comparables} netSheet={result} panel={panel} />
            )}
          </>
        )}
      </div>
      <LeadCaptureModal open={modalOpen} onOpenChange={setModalOpen} onSubmit={handleReportSubmit} />
    </Shell>
  );
}

const Shell = ({ children }) => (
  <div className="min-h-screen bg-brand-cream paper-grain flex flex-col">
    <Header />
    <main className="flex-1">{children}</main>
    <Footer />
  </div>
);
const Center = ({ children }) => (
  <div className="mx-auto max-w-7xl px-5 py-32 flex flex-col items-center text-center">{children}</div>
);
